import { ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { DocumentNode, CheckTestContent, ProcedureDocument, Company } from '@completion/models';
import { selectEditMode, getCurrentDocument, getCompanies } from '@completion/selectors';
import { CompanyLoad, DocumentUpdate } from '@completion/actions';

@Component({
  selector: 'app-check-test',
  templateUrl: './check-test.component.html',
  styleUrls: ['./check-test.component.scss'],
  standalone: false,
})
export class CheckTestComponent implements OnDestroy {
  documentNode: DocumentNode;
  editMode = false;
  data: CheckTestContent;
  companies$: Observable<Company[]> = of([]);

  /** True while the user is typing — prevents store subscription from overwriting local edits. */
  private editing = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject('documentNode') documentNode: DocumentNode,
    @Inject('node') private procedureDocument: ProcedureDocument,
    private store: Store,
    private cdr: ChangeDetectorRef,
  ) {
    this.documentNode = documentNode;
    this.data = this.parseContent(documentNode.content);

    this.store
      .select(selectEditMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((m) => {
        this.editMode = m;
        if (m) {
          // Prefetch companies when entering edit mode
          this.store.dispatch(new CompanyLoad('O'));
          this.companies$ = this.store.select(getCompanies);
        }
      });

    // Keep in sync with store changes — but only when not actively editing
    this.store
      .select(getCurrentDocument)
      .pipe(takeUntil(this.destroy$))
      .subscribe((doc) => {
        if (!doc || this.editing) {
          return;
        }
        const updated = this.findNodeById(doc.nodes || [], this.documentNode.id);
        if (updated) {
          // Only overwrite local data if the stored content actually differs
          // from what we have locally — prevents our own dispatches from wiping edits
          const currentSerialized = JSON.stringify(this.data);
          if (updated.content !== currentSerialized) {
            this.documentNode = updated;
            this.data = this.parseContent(updated.content);
            try {
              this.cdr.markForCheck();
            } catch {}
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Called when the user focuses an input field. */
  onFocus(): void {
    this.editing = true;
  }

  /** Called when the user leaves an input field — save & allow store updates again. */
  onBlur(): void {
    this.save();
    // Small delay so the store update from save() doesn't immediately overwrite
    setTimeout(() => {
      this.editing = false;
    }, 300);
  }

  /** Called when the user selects a company from the dropdown. */
  onCompanySelected(company: Company): void {
    this.editing = true;
    this.data.executorCompany = company;
    this.save();
    setTimeout(() => {
      this.editing = false;
    }, 300);
  }

  /** Persist changes by dispatching DocumentUpdate to the store directly. */
  save(): void {
    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((doc) => {
        if (!doc) {
          return;
        }
        const serialised = JSON.stringify(this.data);
        const updatedNodes = this.updateNodeContent(doc.nodes || [], this.documentNode.id, serialised);
        const updatedDoc = { ...doc, nodes: updatedNodes };
        this.store.dispatch(new DocumentUpdate(updatedDoc));
      });
  }

  // ── helpers ──────────────────────────────────────────────────────────────

  private parseContent(raw: string): CheckTestContent {
    if (!raw) {
      return { name: '', executorCompany: null, sign: null, comments: '' };
    }
    try {
      return JSON.parse(raw) as CheckTestContent;
    } catch {
      return { name: raw, executorCompany: null, sign: null, comments: '' };
    }
  }

  private findNodeById(nodes: DocumentNode[], id: number): DocumentNode | null {
    for (const n of nodes) {
      if (n.id === id) {
        return n;
      }
      if (n.children?.length) {
        const found = this.findNodeById(n.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  private updateNodeContent(nodes: DocumentNode[], id: number, content: string): DocumentNode[] {
    return nodes.map((n) => {
      if (n.id === id) {
        return { ...n, content };
      }
      if (n.children?.length) {
        const updatedChildren = this.updateNodeContent(n.children, id, content);
        if (updatedChildren !== n.children) {
          return { ...n, children: updatedChildren };
        }
      }
      return n;
    });
  }
}
